import { Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import type { ResponsiveStyleValue } from '@mui/system';
import { type MaskOptions, useMask } from '@react-input/mask';
import {
  type ComponentPropsWithoutRef,
  type ComponentPropsWithRef,
  type Ref,
  useId,
} from 'react';
import Icon, { type IconName } from '../../../icon/Icon';
import { ADORNMENT_ICON_SIZE_PX, useStyles } from './TextField.styles';
import { TextFieldSize } from './types';

export type TextFieldProps = Omit<ComponentPropsWithoutRef<'input'>, 'size'> & {
  label?: React.ReactNode;
  helperText?: React.ReactNode;
  error?: boolean;
  fullWidth?: boolean;
  inputSize?: ResponsiveStyleValue<TextFieldSize>;
  mask?: MaskOptions;
  multiline?: boolean;
  rows?: number;
  prefix?: React.ReactNode;
  prefixIcon?: IconName;
  suffix?: React.ReactNode;
  suffixIcon?: IconName;
  ref?: Ref<HTMLInputElement | HTMLTextAreaElement>;
};

function FieldAdornment({
  iconName,
  iconFirst,
  children,
  className,
}: {
  iconName?: IconName;
  iconFirst: boolean;
  children?: React.ReactNode;
  className: string;
}) {
  if (!iconName && children == null) {
    return null;
  }

  const icon = iconName ? (
    <Icon
      name={iconName}
      width={ADORNMENT_ICON_SIZE_PX}
      height={ADORNMENT_ICON_SIZE_PX}
      aria-hidden
    />
  ) : null;

  return (
    <span className={className}>
      {iconFirst ? icon : null}
      {children}
      {iconFirst ? null : icon}
    </span>
  );
}

export default function TextField({
  id,
  ref,
  label,
  helperText,
  error,
  fullWidth = false,
  className,
  inputSize = TextFieldSize.Large,
  mask,
  multiline = false,
  rows = 4,
  prefix,
  prefixIcon,
  suffix,
  suffixIcon,
  ...inputProps
}: TextFieldProps) {
  const { classes, cx } = useStyles({
    fullWidth,
    inputSize,
    multiline,
  });
  const autoId = useId();
  const fieldId = id ?? `text-field-${autoId}`;
  const helperId = `${fieldId}-helper`;
  const maskRef = useMask(multiline ? undefined : mask);

  const hasPrefix = !multiline && (prefix != null || prefixIcon != null);
  const hasSuffix = !multiline && (suffix != null || suffixIcon != null);

  const setFieldRef = (
    element: HTMLInputElement | HTMLTextAreaElement | null
  ) => {
    if (typeof ref === 'function') {
      ref(element);
    } else if (ref) {
      ref.current = element;
    }

    if (!multiline && mask && element instanceof HTMLInputElement) {
      maskRef.current = element;
    }
  };

  const handlePointerDown = (
    event: React.PointerEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    event.currentTarget.setAttribute('data-pointer-focus', '');
    inputProps.onPointerDown?.(event as React.PointerEvent<HTMLInputElement>);
  };

  const handleBlur = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    event.currentTarget.removeAttribute('data-pointer-focus');
    inputProps.onBlur?.(event as React.FocusEvent<HTMLInputElement>);
  };

  const sharedFieldProps = {
    ...inputProps,
    id: fieldId,
    placeholder: inputProps.placeholder ?? '',
    'data-text-field': true,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': helperText ? helperId : undefined,
    onPointerDown: handlePointerDown,
    onBlur: handleBlur,
  };

  return (
    <Stack className={cx(classes.root, className)} gap='6px'>
      {label && (
        <Typography variant='bodyMd' component='label' htmlFor={fieldId}>
          {label}
        </Typography>
      )}
      <div className={classes.control}>
        {hasPrefix ? (
          <FieldAdornment
            iconName={prefixIcon}
            iconFirst
            className={cx(classes.adornment, classes.prefixAdornment)}
          >
            {prefix}
          </FieldAdornment>
        ) : null}
        {multiline ? (
          <textarea
            {...(sharedFieldProps as unknown as ComponentPropsWithRef<'textarea'>)}
            ref={setFieldRef}
            className={cx(classes.input, classes.textarea)}
            rows={rows}
          />
        ) : (
          <input
            {...sharedFieldProps}
            ref={setFieldRef}
            className={cx(classes.input, {
              [classes.inputWithLeftAdornment]: hasPrefix,
              [classes.inputWithRightAdornment]: hasSuffix,
            })}
          />
        )}
        {hasSuffix ? (
          <FieldAdornment
            iconName={suffixIcon}
            iconFirst={false}
            className={cx(classes.adornment, classes.suffixAdornment)}
          >
            {suffix}
          </FieldAdornment>
        ) : null}
      </div>
      {helperText && (
        <Typography
          variant='bodySm'
          id={helperId}
          role={error ? 'alert' : undefined}
          className={classes.helper}
        >
          {helperText}
        </Typography>
      )}
    </Stack>
  );
}
